import { Link, graphql } from 'gatsby'
import { getSrc } from 'gatsby-plugin-image'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { ExtendedH6 } from '../components/Typography/Typography'
import {
  Breadcrumbs,
  H1Styled,
  Left,
  MetaContainer,
  MetaContainerWrapper,
  Right,
} from '../components/atoms/style-helpers'
import theme from '../components/atoms/theme'
import Layout from '../components/layout'
import SEO from '../components/molecules/seo'
import { ContentSet } from '../components/organisms/contentSet'
import FeaturedHeader from '../components/organisms/featuredHeader'
import {
  accessToken,
  contentfulGraphQLUrl,
  currentUtcDateTime,
} from '../lib/contentful'

const fetchLatestEvents = async () => {
  const query = `
    query ($currentDateTime: DateTime!) {
      eventCollection(
        limit: 3
        where: {
          endDate_gte: $currentDateTime
          eventType: { slug: "event" }
        }
        order: [startDate_ASC]
      ) {
        items {
          preTitle
          title
          slug
          startDate
          endDate
          customDateText
          eventType {
            title
            slug
          }
          eventType {
            title
            slug
          }
          categoriesCollection {
            items {
              title
              slug
              colour
            }
          }
          heroImage {
            title
            url
          }
          imageWithFocalPoint {
            image {
              title
              size
              url
              width
              height
            }
            focalPoint
          }
        }
      }
    }`

  const res = await fetch(contentfulGraphQLUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      query,
      variables: { currentDateTime: currentUtcDateTime() },
    }),
  })

  const data = await res.json()

  if (res.ok) {
    data.data.eventCollection.items.map((item) => {
      item.categories = item.categoriesCollection.items
    })

    return data.data.eventCollection.items
  }
  throw new Error(data.errors)
}

const { colours, font, breakpoints } = theme

export const FeaturedTag = styled.div`
  position: absolute;
  background-color: ${colours.primary};
  top: 0;
  transform: translateY(-100%);
  font-size: 1.6rem;
  font-weight: ${font.fontWeightBold};
  padding: 1.4rem 3.8rem;

  @media only screen and ${breakpoints.toNormalScreen} {
    padding: 1.4rem 2rem;
  }
`

export const LeadingParagraph = styled.div`
  margin-bottom: 2rem;
  text-align: left;
`

export const MetaSectionBottom = styled.div`
  display: flex;
  font-size: 1.4rem;
  line-height: 1.4;

  @media only screen and ${breakpoints.toNormalScreen} {
    flex-direction: column;
  }

  h6,
  ${ExtendedH6} {
    margin: 0 2.4rem 0 0;

    @media only screen and ${breakpoints.toNormalScreen} {
      margin: 0 2.4rem 1rem 0;
    }
  }
`

export const H1StyledLeft = styled(H1Styled)`
  text-align: left;
`

export const GreyBackground = styled.div`
  background-color: ${colours.lightTint4};
  padding-bottom: 2rem;
`

type WhatsOnPageProps = {
  data: Queries.WhatsOnPageQuery
}
const WhatsOn = ({ data }: WhatsOnPageProps): JSX.Element => {
  const featuredEvent = data?.featuredEvent?.edges[0]?.node
  const eventsSectionItems = data?.EventsSectionItems?.edges
  const exhibitionsSectionItems = data?.ExhibitionsSectionItems?.edges
  const experiencesSectionItems = data?.ExperiencesSectionItems?.edges

  const [events, setEvents] = useState(eventsSectionItems)

  useEffect(() => {
    fetchLatestEvents()
      .then((data) => {
        setEvents(data)
      })
      .catch((error) => {
        console.error('Error fetching data:', error)
      })
  }, [])

  return (
    <Layout>
      <GreyBackground>
        <MetaContainer>
          <Breadcrumbs>
            <Link to='/'>Home /</Link> What&apos;s on
          </Breadcrumbs>
          <MetaContainerWrapper>
            <Left>
              <H1Styled>What&apos;s on</H1Styled>
            </Left>
            <Right>
              See what&apos;s happening at MOTAT this month. Take a look at our
              latest events and some of the exhibitions and experiences you can
              enjoy when you visit.
            </Right>
          </MetaContainerWrapper>
        </MetaContainer>
        {featuredEvent && <FeaturedHeader event={featuredEvent} />}
        {events && (
          <ContentSet title='Events' allLink='/events/' nodes={events} />
        )}
        {exhibitionsSectionItems && (
          <ContentSet
            title='Exhibitions'
            allLink='/exhibitions/'
            nodes={exhibitionsSectionItems}
          />
        )}
        {experiencesSectionItems && (
          <ContentSet
            title='Experiences'
            allLink='/experiences/'
            nodes={experiencesSectionItems}
          />
        )}
      </GreyBackground>
    </Layout>
  )
}

export default WhatsOn

export const Head = ({ data }: WhatsOnPageProps) => {
  const featuredEvent = data?.featuredEvent?.edges[0]?.node
  const imageUrl = featuredEvent?.heroImage && getSrc(featuredEvent?.heroImage)
  return (
    <SEO
      title="What's on"
      description="See what's happening at MOTAT with our latest events..."
      imageUrl={imageUrl}
    />
  )
}

export const pageQuery = graphql`
  query WhatsOnPage {
    featuredEvent: allContentfulEvent(
      filter: { isFeatured: { eq: true }, eventType: { slug: { eq: "event" } } }
      sort: { startDate: ASC }
      limit: 1
    ) {
      edges {
        node {
          preTitle
          title
          slug
          heroImage {
            title
            url
            gatsbyImageData(layout: CONSTRAINED, quality: 80, placeholder: BLURRED, formats: [WEBP])
          }
          leadingParagraph {
            childMarkdownRemark {
              rawMarkdownBody
            }
          }
          categories {
            title
            slug
          }
          startDate
          endDate
          customDateText
          eventType {
            title
            slug
          }
          imageWithFocalPoint {
            focalPoint {
              focalPoint {
                x
                y
              }
            }
            image {
              file {
                details {
                  image {
                    height
                    width
                  }
                }
              }
              url
              gatsbyImageData(layout: CONSTRAINED, quality: 80)
            }
          }
        }
      }
    }
    EventsSectionItems: allContentfulEvent(
      limit: 3
      sort: { startDate: ASC }
      filter: { eventType: { slug: { eq: "event" } } }
    ) {
      edges {
        node {
          id
          preTitle
          title
          slug
          startDate
          endDate
          customDateText
          eventType {
            title
            slug
          }
          eventType {
            title
            slug
          }
          categories {
            title
            slug
            id
            colour
          }
          heroImage {
            url
            gatsbyImageData(
              layout: CONSTRAINED
              width: 480
              height: 300
              quality: 80
              placeholder: BLURRED
              formats: [WEBP]
            )
            title
          }
          imageWithFocalPoint {
            focalPoint {
              focalPoint {
                x
                y
              }
            }
            image {
              file {
                details {
                  image {
                    height
                    width
                  }
                }
              }
              url
              gatsbyImageData(layout: CONSTRAINED, width: 480, height: 300, quality: 80)
            }
          }
        }
      }
    }
    ExhibitionsSectionItems: allContentfulEvent(
      limit: 3
      sort: { createdAt: ASC }
      filter: { eventType: { slug: { eq: "exhibition" } } }
    ) {
      edges {
        node {
          id
          preTitle
          title
          slug
          startDate
          endDate
          customDateText
          eventType {
            title
            slug
          }
          eventType {
            title
            slug
          }
          categories {
            title
            slug
            id
            colour
          }
          heroImage {
            url
            gatsbyImageData(
              layout: CONSTRAINED
              width: 480
              height: 300
              quality: 80
              placeholder: BLURRED
              formats: [WEBP]
            )
            title
          }
          imageWithFocalPoint {
            focalPoint {
              focalPoint {
                x
                y
              }
            }
            image {
              file {
                details {
                  image {
                    height
                    width
                  }
                }
              }
              url
              gatsbyImageData(layout: CONSTRAINED, width: 480, height: 300, quality: 80)
            }
          }
        }
      }
    }
    ExperiencesSectionItems: allContentfulEvent(
      limit: 3
      sort: { createdAt: ASC }
      filter: { eventType: { slug: { eq: "experience" } } }
    ) {
      edges {
        node {
          id
          preTitle
          title
          slug
          startDate
          endDate
          customDateText
          eventType {
            title
            slug
          }
          eventType {
            title
            slug
          }
          categories {
            title
            slug
            id
            colour
          }
          heroImage {
            url
            gatsbyImageData(
              layout: CONSTRAINED
              width: 480
              height: 300
              quality: 80
              placeholder: BLURRED
              formats: [WEBP]
            )
            title
          }
          imageWithFocalPoint {
            focalPoint {
              focalPoint {
                x
                y
              }
            }
            image {
              file {
                details {
                  image {
                    height
                    width
                  }
                }
              }
              url
              gatsbyImageData(layout: CONSTRAINED, width: 480, height: 300, quality: 80)
            }
          }
        }
      }
    }
  }
`
